// DS — powierzchnia medialna (artboard 04): PRAWDZIWY KADR ALBO STAN.
//
// Reguła do lintu (MAPA §Zdania do lintu, 2): element `.media` zawiera dokładnie
// jedno z: <img>/<video> z adresu backendu (thumb_url, pierwsza klatka klipu,
// still stylu) ALBO stan ze zdaniem (+ postęp, gdy N/M znane). Gradient jako tło,
// import z lib/grads.ts, gradFor/clipGrad/mediaBg = błąd lintu.
//
// Typ wymusza to samo: bez `src` trzeba podać `state` (albo `wave` dla utworu).

import type { ReactNode } from 'react'
import { Glyph, type GlyphName } from './Glyph'
import { Progress } from '../primitives/Progress'

export type MediaRatio = '4:3' | '9:16' | '1:1' | '16:9'

const RATIO_CLASS: Record<MediaRatio, string> = { '4:3': 'r-43', '9:16': 'r-916', '1:1': 'r-11', '16:9': 'r-169' }

export type MediaState = {
  /** Co się dzieje / dlaczego kadru nie ma — słowo lub krótkie zdanie (pogrubione). */
  title: string
  /** Powód albo czas: „3 z 8”, „paczka jest pusta”. */
  text?: string
  /** Glif stanu (48 → w powierzchni 32). */
  glyph?: GlyphName
  /** Postęp 0..1, gdy N/M znane — bez kręciołka. */
  progress?: number
}

type Common = {
  ratio?: MediaRatio
  /** Podpis na kadrze (lewy dół, na własnym półprzezroczystym pasie). */
  caption?: ReactNode
  /** Czas trwania w prawym dolnym rogu (mono). */
  duration?: string
  className?: string
  children?: ReactNode
}

type WithImage = Common & { src: string; alt: string; /** Gęstość 2× (`@2x`). */ srcSet?: string; /** Obraz dekoracyjny wewnątrz kontrolki nie powinien uruchamiać natywnego przeciągania. */ draggable?: boolean; /** Wygasły podpisany adres: ekran przełącza się na stan (sesja 09). */ onError?: () => void; state?: undefined; wave?: undefined }
type WithVideo = Common & { src: string; video: true; alt?: undefined; poster?: string; state?: undefined; wave?: undefined }
type WithState = Common & { src?: undefined; state: MediaState; wave?: undefined }
type WithWave = Common & { src?: undefined; state?: undefined; wave: number[]; played?: number; alt?: string }

export type MediaProps = WithImage | WithVideo | WithState | WithWave

export function Media(props: MediaProps) {
  const { ratio = '16:9', caption, duration, className = '', children } = props
  const cls = ['media', RATIO_CLASS[ratio], className].filter(Boolean).join(' ')

  let body: ReactNode
  if ('src' in props && props.src) {
    if ('video' in props && props.video) {
      body = <video src={props.src} poster={props.poster} muted playsInline loop autoPlay />
    } else {
      body = <img src={props.src} srcSet={(props as WithImage).srcSet} alt={(props as WithImage).alt} draggable={(props as WithImage).draggable} loading="lazy" decoding="async" onError={(props as WithImage).onError} />
    }
  } else if ('wave' in props && props.wave) {
    body = <Wave peaks={props.wave} played={props.played} label={props.alt} />
  } else {
    const s = (props as WithState).state
    body = (
      <div className="media-state">
        {s.glyph ? <Glyph name={s.glyph} size={32} tone="current" /> : null}
        <b>{s.title}</b>
        {s.text ? <span>{s.text}</span> : null}
        {typeof s.progress === 'number' ? <Progress value={s.progress} onStage /> : null}
      </div>
    )
  }

  return (
    <div className={cls} data-media={'src' in props && props.src ? 'frame' : 'state'}>
      {body}
      {children}
      {caption ? <div className="media-cap">{caption}</div> : null}
      {duration ? <span className="media-dur">{duration}</span> : null}
    </div>
  )
}

export interface WaveProps {
  /** Prawdziwe szczyty 0..1 (z analizy utworu), nie atrapa. */
  peaks: number[]
  /** Ułamek odtworzony 0..1 — słupki przed nim mają kolor akcentu. */
  played?: number
  label?: string
  className?: string
}

/** Fala utworu: słupki z prawdziwych próbek; odtworzone → --accent (na scenie --stage-fg). */
export function Wave({ peaks, played = 0, label, className = '' }: WaveProps) {
  const cut = Math.floor(played * peaks.length)
  return (
    <div className={['wave', className].filter(Boolean).join(' ')} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      {peaks.map((p, i) => (
        <i key={i} className={i < cut ? 'is-played' : undefined} style={{ height: `${Math.max(10, Math.min(100, p * 100))}%` }} />
      ))}
    </div>
  )
}

export interface MediaCardProps {
  media: ReactNode
  name: ReactNode
  /** Pigułka stanu obok nazwy. */
  status?: ReactNode
  meta?: ReactNode
  className?: string
}

/** Karta mediów: media + nazwa + pigułka + meta (edit · utwór · paczka · klip). */
export function MediaCard({ media, name, status, meta, className = '' }: MediaCardProps) {
  return (
    <div className={['mcard', className].filter(Boolean).join(' ')}>
      {media}
      <div className="mcard-name"><span>{name}</span>{status}</div>
      {meta ? <div className="mcard-meta">{meta}</div> : null}
    </div>
  )
}

export default Media
